import React, { useEffect, useMemo, useRef, useState } from 'react';

const MASK = '••••••••';

/**
 * PropertiesSection — a generic key/value editor for the "Properties" section of
 * the Node Edit + Station Edit dialogs (admin-node-station-edit slug).
 *
 * It renders the entries of `properties` (a plain {key: value} map) MINUS any
 * key in `ownedKeys` (those are edited by a dedicated section elsewhere).
 *
 * Two editability modes:
 *
 *  - **Allowlist (fail-closed)** — pass `editableKeys`. A key is editable IFF it
 *    is in `editableKeys`; EVERYTHING ELSE is read-only. This is the node mode:
 *    node_prop carries credentials (apikey/token) and handshake/auth wiring, so
 *    only an explicit allowlist (`accepted_providers`) may be edited and a newly
 *    introduced category defaults to read-only, not editable. "Add property" is
 *    hidden (an arbitrary new node_prop key is rejected server-side anyway).
 *
 *  - **Denylist** — omit `editableKeys`; pass `criticalKeys` (or neither). A key
 *    is editable unless it is in `criticalKeys`. This is the station mode: the
 *    station config has no credential-bearing keys, so non-owned keys stay
 *    freely editable and "Add property" is available.
 *
 * `maskedKeys` (e.g. credentials): the value is shown as •••••••• and is ALWAYS
 * read-only. The raw value is NEVER read into row state or the DOM, and a masked
 * key is excluded from the emitted patch (so the mask string can't be saved).
 *
 * Pure editor — it owns no save. On every change it calls `onPatchChange(patch)`
 * where `patch` is the set of changes vs. the initial map: {key: newValue} for
 * edits/additions and {key: null} for removals. Read-only and masked keys NEVER
 * appear in the patch. The host merges it into its own save body (station config
 * jsonb shallow-merge, or PATCH node_prop).
 *
 * Values are parsed leniently on save: a value that JSON-parses (numbers,
 * booleans, objects) is sent typed; otherwise as the raw string.
 */
export default function PropertiesSection({
  properties, ownedKeys, criticalKeys, editableKeys, maskedKeys, onPatchChange,
}) {
  const owned = useMemo(() => new Set(ownedKeys || []), [ownedKeys]);
  const critical = useMemo(() => new Set(criticalKeys || []), [criticalKeys]);
  const masked = useMemo(() => new Set(maskedKeys || []), [maskedKeys]);
  // Allowlist mode iff editableKeys was provided (even if empty → all read-only).
  const allowlistMode = Array.isArray(editableKeys);
  const editable = useMemo(() => new Set(editableKeys || []), [editableKeys]);

  // A key is read-only if masked, or (allowlist) not in editable, or (denylist)
  // in critical. Fail-closed: an unknown key in allowlist mode is read-only.
  const keyReadOnly = useMemo(() => (rawKey) => {
    const k = (rawKey || '').trim();
    if (masked.has(k)) return true;
    return allowlistMode ? !editable.has(k) : critical.has(k);
  }, [masked, allowlistMode, editable, critical]);

  // Initial entries = properties minus owned keys. Masked keys carry NO value
  // (the raw secret must never enter state) — they render as the mask glyph.
  const initialEntries = useMemo(() => {
    const out = [];
    Object.entries(properties || {}).forEach(([k, v]) => {
      if (owned.has(k)) return;
      out.push({ key: k, valueStr: masked.has(k) ? '' : toDisplay(v), masked: masked.has(k) });
    });
    return out;
  }, [properties, owned, masked]);

  const [rows, setRows] = useState(() => initialEntries.map((e, i) => ({
    id: `r${i}`, key: e.key, valueStr: e.valueStr, masked: e.masked, isNew: false,
  })));
  const [removed, setRemoved] = useState(() => new Set());   // editable keys removed
  const nextId = useRef(initialEntries.length);
  const lastPatchSig = useRef(null);

  const initialByKey = useMemo(() => {
    const m = {};
    initialEntries.forEach((e) => { m[e.key] = e.valueStr; });
    return m;
  }, [initialEntries]);

  // Recompute the patch. Read-only + masked keys are EXCLUDED — a read-only row
  // can never contribute a change (and a masked row would otherwise leak the
  // mask string). Removals are tracked only for editable keys.
  useEffect(() => {
    const patch = {};
    removed.forEach((k) => { patch[k] = null; });
    rows.forEach((row) => {
      const k = (row.key || '').trim();
      if (!k) return;
      if (row.masked || keyReadOnly(k)) return;          // never patch read-only/masked
      if (!row.isNew && row.valueStr === initialByKey[k]) return;   // unchanged
      patch[k] = parseValue(row.valueStr);
    });
    // Report only a CHANGED patch. Callers pass fresh literals (`criticalKeys={[]}`,
    // `properties={config || {}}`) and store the patch in state, so reporting on every
    // effect run looped forever (render → new keyReadOnly → effect → setState → render):
    // "Maximum update depth exceeded" the moment the Station Edit → Properties tab
    // mounted (found by station-private-log-redaction's first test that opens it).
    const sig = JSON.stringify(patch);
    if (sig === lastPatchSig.current) return;
    lastPatchSig.current = sig;
    if (onPatchChange) onPatchChange(patch);
  }, [rows, removed, initialByKey, keyReadOnly, onPatchChange]);

  const setValue = (id, valueStr) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, valueStr } : r)));
  const setKey = (id, key) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, key } : r)));
  const removeRow = (row) => {
    setRows((rs) => rs.filter((r) => r.id !== row.id));
    if (!row.isNew) setRemoved((s) => new Set(s).add(row.key));
  };
  const addRow = () => {
    const id = `n${nextId.current++}`;
    setRows((rs) => [...rs, { id, key: '', valueStr: '', masked: false, isNew: true }]);
  };

  const editableRows = rows.filter((r) => !r.masked && !keyReadOnly(r.key));

  return (
    <div>
      <p style={{ fontSize: '0.8rem', color: '#666', marginTop: 0 }}>
        {allowlistMode ? (
          <>
            Most node properties are <strong>read-only</strong> — they back the
            node's handshake / auth and are managed by their own flow. Only an
            allowlisted few are operator-editable; credential values are masked.
            Values that look like JSON are stored typed, everything else as text.
          </>
        ) : (
          <>
            Raw properties not owned by another section. Values that look like JSON
            (numbers, booleans, objects) are stored typed; everything else as text.
          </>
        )}
      </p>
      {rows.length === 0 && (
        <div style={{ color: '#aaa', fontSize: '0.85rem', fontStyle: 'italic' }}>No properties.</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((row) => {
          const ro = row.masked || keyReadOnly(row.key);
          return (
            <div key={row.id} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {row.isNew ? (
                <input placeholder="key" value={row.key}
                  onChange={(e) => setKey(row.id, e.target.value)}
                  style={{ ...inputStyle, flex: '0 0 34%' }} />
              ) : (
                <code style={{ flex: '0 0 34%', fontSize: '0.78rem', overflowWrap: 'anywhere' }}>
                  {ro && <span title={row.masked ? 'Credential — masked; read-only' : 'Managed — read-only'} style={{ marginRight: 4 }}>🔒</span>}
                  {row.key}
                </code>
              )}
              <input value={row.masked ? MASK : row.valueStr} disabled={ro}
                onChange={(e) => setValue(row.id, e.target.value)}
                placeholder={ro ? '(managed)' : 'value'}
                style={{ ...inputStyle, flex: 1, background: ro ? '#f4f4f4' : '#fff' }} />
              {!ro && (
                <button type="button" onClick={() => removeRow(row)} title="remove"
                  style={removeBtn}>✕</button>
              )}
            </div>
          );
        })}
      </div>
      {!allowlistMode && (
        <button type="button" onClick={addRow} style={addBtn}>+ add property</button>
      )}
      {editableRows.length === 0 && rows.length > 0 && (
        <div style={{ fontSize: '0.75rem', color: '#999', marginTop: 6 }}>
          {allowlistMode
            ? 'All properties shown are read-only.'
            : 'All current properties are critical (read-only).'}
        </div>
      )}
    </div>
  );
}

// Display a stored value for editing: objects/arrays → JSON; primitives → string.
function toDisplay(v) {
  if (v == null) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// Parse an edited string back to a typed value: JSON if it parses, else string.
function parseValue(str) {
  const s = str == null ? '' : String(str);
  if (s === '') return '';
  try { return JSON.parse(s); } catch (_e) { return s; }
}

const inputStyle = {
  padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.82rem',
  minWidth: 0,
};
const removeBtn = {
  flex: '0 0 auto', padding: '4px 8px', background: 'transparent', color: '#c0392b',
  border: '1px solid #e0c2c2', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer',
};
const addBtn = {
  marginTop: 10, padding: '5px 12px', background: 'transparent', color: '#5fa67a',
  border: '1px solid #5fa67a', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer',
};
